// Guards the lazy document-rows chunk. Lives in the MAIN bundle — it must not
// import anything from the chunk it guards (DocumentRows, documentUtils).
//
//   - DocumentsBoundary: a chunk 404 (stale HTML after a deploy, flaky network)
//     or a render crash inside the rows must never take the portal down. The
//     attachment card stays, its uploaded files keep working, and `fallback`
//     takes the rows' place (PortalClient passes "This file is currently
//     unavailable." — never "No files": the header counts the templates).
//     Warned once per card.
//   - DocumentRowsFallback: the Suspense placeholder — one row of the same
//     height per template, name already visible, so the card does not jump
//     when the chunk lands (a text pill's height, a WALLET row the 48 px
//     official badge's).

import { Component, type CSSProperties, type ReactNode } from "react";
import type { DocumentEntry } from "@/types/Attachment";

type BoundaryProps = { children: ReactNode; fallback: ReactNode };
type BoundaryState = { failed: boolean };

export class DocumentsBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { failed: false };
  private warned = false;

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    if (this.warned) return;
    this.warned = true;
    console.warn("[portal] document rows failed to load — rendering the attachment without them", error);
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function DocumentRowsFallback({
  documents,
  secondaryStyle,
}: {
  documents: DocumentEntry[];
  secondaryStyle: CSSProperties;
}) {
  return (
    <div data-testid="portal-doc-fallback" aria-busy="true">
      {documents.map((d) => (
        <div key={d.templateId} className="flex items-center gap-3 px-5 py-3">
          <span className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-sm">{d.name}</span>
          <span
            className={`${d.documentType === "WALLET" ? "h-12 w-44" : "h-9 w-24"} shrink-0 rounded-full bg-current opacity-10 motion-safe:animate-pulse motion-reduce:animate-none`}
            style={secondaryStyle}
          />
        </div>
      ))}
    </div>
  );
}
